
interface Retailer {
  name: string;
  price: number;
  url: string;
  logo: string;
  inStock: boolean;
}

interface RetailerComparisonProps {
  retailers: Retailer[];
  productName: string;
}

const RetailerComparison = ({ retailers, productName }: RetailerComparisonProps) => {
  const sortedRetailers = [...retailers].sort((a, b) => a.price - b.price);
  const lowestPrice = sortedRetailers[0]?.price || 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Compare Prices</h3>
        <span className="text-sm text-gray-500">{retailers.length} retailers</span>
      </div>

      <div className="space-y-3">
        {sortedRetailers.map((retailer, index) => {
          const isLowest = retailer.price === lowestPrice;
          const priceDiff = retailer.price - lowestPrice;

          return (
            <a
              key={retailer.name}
              href={retailer.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between p-4 rounded-lg border transition-all cursor-pointer ${
                isLowest
                  ? 'border-teal-200 bg-teal-50 hover:border-teal-300'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isLowest ? 'bg-teal-100' : 'bg-gray-100'
                }`}>
                  <i className={`${retailer.logo} text-xl ${isLowest ? 'text-teal-600' : 'text-gray-600'}`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{retailer.name}</span>
                    {isLowest && (
                      <span className="px-2 py-0.5 bg-teal-600 text-white text-xs rounded-full">Best Price</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {retailer.inStock ? (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill"></i>
                        In Stock
                      </span>
                    ) : (
                      <span className="text-xs text-red-500 flex items-center gap-1">
                        <i className="ri-close-circle-fill"></i>
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className={`text-xl font-bold ${isLowest ? 'text-teal-600' : 'text-gray-900'}`}>
                  ${retailer.price}
                </div>
                {!isLowest && priceDiff > 0 && (
                  <div className="text-xs text-gray-500">
                    +${priceDiff} more
                  </div>
                )}
              </div>
            </a>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-information-line text-xl text-gray-400"></i>
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">Price Comparison</p>
            <p>Prices are updated hourly. Click on a retailer to visit their website and complete your purchase.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerComparison;
